import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { TrackDetailView, Toast, useToast, ConfirmationModal, useConfirmation } from '../../components/shared';
import { useTranslation } from 'react-i18next';

const TrackDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { releases, deleteRelease, submitForApproval } = useData();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { toasts, hideToast, showError, showSuccess } = useToast();
  const { confirmationState, showConfirmation, handleCancel } = useConfirmation();
  const navigate = useNavigate();
  
  const release = releases.find(r => r.id === id && r.artist_id === user?.id);

  if (!release) {
    return (
      <div className="glass-card text-center py-12">
        <h2 className="text-2xl font-bold text-white">{t('tracks.notFound')}</h2>
        <p className="mt-2 text-white/70">{t('tracks.noAccess')}</p>
        <Link to="/tracks" className="mt-4 btn-primary inline-block">
          {t('tracks.backToTracks')}
        </Link>
      </div>
    );
  }

  const handleDelete = async () => {
    const confirmed = await showConfirmation({
      title: t('tracks.deleteConfirmTitle'),
      message: t('tracks.deleteConfirmMessage'),
      confirmText: t('common.delete'),
      type: 'danger'
    });

    if (confirmed) {
      const success = await deleteRelease(release.id);
      if (success) {
        showSuccess(t('tracks.deleteSuccess'));
        navigate('/tracks');
      } else {
        showError(t('tracks.deleteError'));
      }
    }
  };

  const handleSubmitForApproval = async () => {
    const confirmed = await showConfirmation({
      title: t('tracks.submitConfirmTitle'),
      message: t('tracks.submitConfirmMessage'),
      confirmText: t('tracks.submitForApproval'),
      type: 'info'
    });

    if (confirmed) {
      const success = await submitForApproval(release.id);
      if (success) {
        showSuccess(t('tracks.submitSuccess'));
      } else {
        showError(t('tracks.submitError'));
      }
    }
  };

  // Edit yetkisini kontrol et: sadece draft veya rejected durumunda
  const canEdit = release.status === 'draft' || release.status === 'rejected';
  
  // Silme yetkisini kontrol et: sadece draft durumunda
  const canDelete = release.status === 'draft';

  // Onaya gönder yetkisini kontrol et: sadece draft veya rejected durumunda
  const canSubmitForApproval = release.status === 'draft' || release.status === 'rejected';

  return (
    <>
      <TrackDetailView
        release={release}
        isAdmin={false}
        canEdit={canEdit}
        canDelete={canDelete}
        canSubmitForApproval={canSubmitForApproval}
        onDelete={handleDelete}
        onSubmitForApproval={handleSubmitForApproval}
        backToPath="/tracks"
        backToText="tracks.backToTracks"
      />
      
      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationState.isOpen}
        title={confirmationState.title}
        message={confirmationState.message}
        confirmText={confirmationState.confirmText}
        cancelText={confirmationState.cancelText}
        type={confirmationState.type}
        onConfirm={confirmationState.onConfirm}
        onCancel={handleCancel}
      />
      
      {/* Toast Messages */}
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => hideToast(toast.id)}
        />
      ))}
    </>
  );
};

export default TrackDetail; 